import { produce } from 'immer';
import _ from 'lodash';

import { CUSTOM_FIELDS_ACTION_TYPE } from '../types/customFieldDefinitions.types';

const initialState = {
  customFields: [],
  isLoading: false,
  loading: false, // Added for backward compatibility
  error: null,
};

const mergeIncomingDefinitions = (existing, incoming) => {
  const byId = new Map(existing.map(field => [field.id, field]));

  incoming.forEach(field => {
    if (field?.id) {
      byId.set(field.id, field);
    }
  });

  return Array.from(byId.values());
};

const snapshotGlobalDefaults = field => ({
  description: field.description ?? null,
  isEnabled: _.isBoolean(field.isEnabled) ? field.isEnabled : null,
  defaultValue: _.isNil(field.defaultValue) ? null : field.defaultValue,
  displayInLoanEditor: _.isBoolean(field.valueSource?.displayInLoanEditor)
    ? field.valueSource.displayInLoanEditor
    : null,
  expression: field.valueSource?.expression ?? null,
});

const mergeValueSource = (baseValueSource, displayInLoanEditor, expression) => {
  const source = baseValueSource ?? {};
  const resolvedDisplay = _.isBoolean(displayInLoanEditor)
    ? displayInLoanEditor
    : (source.displayInLoanEditor ?? null);
  const resolvedExpression = _.isNil(expression) ? (source.expression ?? null) : expression;

  return {
    ...source,
    displayInLoanEditor: resolvedDisplay,
    expression: resolvedExpression,
  };
};

const applyOverrideToBase = (field, override) => {
  if (!override) {
    const globals = field.globalDefaults;

    if (!globals) {
      return { ...field, localOverride: null };
    }

    return {
      ...field,
      description: globals.description ?? null,
      isEnabled: _.isBoolean(globals.isEnabled) ? globals.isEnabled : true,
      defaultValue: globals.defaultValue ?? null,
      valueSource: mergeValueSource(
        field.valueSource,
        globals.displayInLoanEditor,
        globals.expression,
      ),
      localOverride: null,
      globalDefaults: null,
    };
  }

  const globals = field.globalDefaults ?? snapshotGlobalDefaults(field);
  const mergedIsEnabled = _.isBoolean(override.isEnabled) ? override.isEnabled : globals.isEnabled;
  const mergedDisplay = _.isBoolean(override.displayInLoanEditor)
    ? override.displayInLoanEditor
    : globals.displayInLoanEditor;
  const mergedExpression = _.isNil(override.expression) ? globals.expression : override.expression;

  return {
    ...field,
    description: override.description ?? globals.description,
    isEnabled: _.isBoolean(mergedIsEnabled) ? mergedIsEnabled : true,
    defaultValue: override.defaultValue ?? globals.defaultValue,
    valueSource: mergeValueSource(field.valueSource, mergedDisplay, mergedExpression),
    localOverride: override,
    globalDefaults: globals,
  };
};

const customFieldsReducer = produce((draft, { type, payload }) => {
  switch (type) {
    case CUSTOM_FIELDS_ACTION_TYPE.SET_CUSTOM_FIELDS:
      draft.customFields = Array.isArray(payload) ? payload : [];
      break;

    case CUSTOM_FIELDS_ACTION_TYPE.MERGE_CUSTOM_FIELDS:
      if (Array.isArray(payload) && payload.length) {
        draft.customFields = mergeIncomingDefinitions(draft.customFields, payload);
      }

      break;

    case CUSTOM_FIELDS_ACTION_TYPE.SET_LOADING:
      draft.isLoading = !!payload;
      draft.loading = !!payload;
      break;

    case CUSTOM_FIELDS_ACTION_TYPE.SET_ERROR:
      draft.error = payload;
      break;

    case CUSTOM_FIELDS_ACTION_TYPE.CLEAR_ERROR:
      draft.error = null;
      break;

    case CUSTOM_FIELDS_ACTION_TYPE.ADD_CUSTOM_FIELD:
      if (payload && payload.id) {
        draft.customFields.push(payload);
      }

      break;

    case CUSTOM_FIELDS_ACTION_TYPE.DELETE_CUSTOM_FIELD:
      if (payload) {
        draft.customFields = draft.customFields.filter(field => field.id !== payload);
      }

      break;

    case CUSTOM_FIELDS_ACTION_TYPE.UPDATE_CUSTOM_FIELD:
      if (payload && payload.id) {
        const index = draft.customFields.findIndex(field => field.id === payload.id);

        if (index !== -1) {
          draft.customFields[index] = {
            ...draft.customFields[index],
            ...payload,
            isEnabled: _.isBoolean(payload.isEnabled)
              ? payload.isEnabled
              : draft.customFields[index].isEnabled,
            isGlobal: _.isBoolean(payload.isGlobal)
              ? payload.isGlobal
              : draft.customFields[index].isGlobal,
          };
        }
      }

      break;

    case CUSTOM_FIELDS_ACTION_TYPE.UPSERT_CUSTOM_FIELD_OVERRIDE: {
      const { id, override } = payload ?? {};
      const index = id ? draft.customFields.findIndex(field => field.id === id) : -1;

      if (index !== -1) {
        draft.customFields[index] = applyOverrideToBase(
          draft.customFields[index],
          override ?? null,
        );
      }

      break;
    }

    case CUSTOM_FIELDS_ACTION_TYPE.DELETE_CUSTOM_FIELD_OVERRIDE: {
      const { id } = payload ?? {};
      const index = id ? draft.customFields.findIndex(field => field.id === id) : -1;

      if (index !== -1) {
        draft.customFields[index] = applyOverrideToBase(draft.customFields[index], null);
      }

      break;
    }

    case CUSTOM_FIELDS_ACTION_TYPE.RESET:
      return initialState;

    default:
      return draft;
  }
}, initialState);

export default customFieldsReducer;
